import React, { useMemo, useState } from "react";

// Third Party
import { Button, Popover, type PopoverProps } from "antd";
import {
  BellOutlined,
  ExportOutlined,
  InboxOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";

// Custom Components
import Anchors from "../Anchors";

// API
import { readAllAlerts } from "../../../services/https/alerts";

// Redux
import { RootState } from "../../../store/store";

// helper
import { convertToReadable } from "../../../helpers";

// Style
import "./style.css";

// Image
import { images } from "../../../assets/images";

interface NotificationPopupProps {
  triggerContent?: React.ReactNode;
  triggerClassName?: string;
  placement?: PopoverProps["placement"];
}

const NOTIFICATION_ICONS: Record<string, React.ReactNode> = {
  status_update: <SyncOutlined />,
  allocated: <InboxOutlined />,
  export: <ExportOutlined />,
};

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  triggerContent,
  triggerClassName = "",
  placement = "bottom",
}) => {
  const { notifications } = useSelector((state: RootState) => state.alerts);

  const content = (
    <div className="notification-list-container text-start rounded rounded-4">
      <div className="notification-list-header">Notifications</div>
      {notifications.length > 0 ? (
        <ul className="notification-list-ul">
          {notifications.map((notification) => (
            <li
              key={notification.id}
              className={`notification-card d-flex align-items-start gap-2 ${
                notification.read ? "read-notification" : "unread-notification"
              }`}
            >
              <span
                className={`notification-card-icon notification-card-icon-${notification.type}`}
              >
                {NOTIFICATION_ICONS[notification.type] || <BellOutlined />}
              </span>
              <div className="notification-card-body">
                {notification.type === "status_update" && (
                  <Anchors
                    to={`/dime-explain/records/${notification.meta_data.record_id}/prepare/`}
                  >
                    {notification.content} to:&nbsp;
                    {convertToReadable(notification.meta_data.checker_status)}
                    {convertToReadable(notification.meta_data.maker_status)}
                  </Anchors>
                )}
                {notification.type === "allocated" && (
                  <p className="support-card__desc">{notification.content}</p>
                )}
                {notification.type === "export" && (
                  <Anchors
                    to={notification.meta_data.file_url}
                    target="_blank"
                  >
                    <p>{notification.content}</p>
                  </Anchors>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="notification-empty-state">
          <BellOutlined className="notification-empty-icon" />
          <p>You&apos;re all caught up</p>
        </div>
      )}
    </div>
  );

  // Ids the user has already looked at by opening the bell. The list keeps
  // highlighting them until it is next fetched, so the badge cannot rely on
  // the `read` flag alone: it would still show after the user had seen them.
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());

  const unreadCount = useMemo(
    () =>
      notifications.filter(
        (notification) => !notification.read && !seenIds.has(notification.id),
      ).length,
    [notifications, seenIds],
  );
  const hasUnreadNotifications = unreadCount > 0;
  // The list holds at most the latest 50, so a large count is shown capped.
  const badgeLabel = unreadCount > 9 ? "9+" : String(unreadCount);

  const handleReadAll = async () => {
    setSeenIds(new Set(notifications.map((notification) => notification.id)));
    readAllAlerts();
  };

  return (
    <div className="notification-popup-wrapper">
      <Popover
        placement={placement}
        className="notification-popup"
        content={content}
        title=""
        trigger="click"
        overlayClassName="notification-popover"
      >
        <Button
          className={`notification-block relative ${triggerClassName}`}
          onClick={handleReadAll}
        >
          {triggerContent || (
            <img className="" src={images.BELL_ICON} alt="Notifications" />
          )}
          {hasUnreadNotifications && (
            <span
              className={`notification-badge-indicator rounded-pill ${
                triggerContent ? "notification-badge-custom-trigger" : ""
              }`}
              aria-label={`${unreadCount} unread notifications`}
              style={{
                minWidth: "16px",
                height: "16px",
                padding: "0 4px",
                background: "red",
                color: "#fff",
                fontSize: "10px",
                lineHeight: "16px",
                fontWeight: 600,
                textAlign: "center",
                position: "absolute",
                top: "-4px",
                right: "-4px",
              }}
            >
              {badgeLabel}
            </span>
          )}
        </Button>
      </Popover>
    </div>
  );
};

export default NotificationPopup;
